import React from 'react';
import { Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

const MAX_DISPLAY_ROWS = 200;

const columnName = (c) => (c && typeof c === 'object' ? c.name : String(c));

const formatCell = (v, locale) => {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'number') return v.toLocaleString(locale, { maximumFractionDigits: 2 });
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
};

/** Plain table view of a widget's rows: used when figure_json is null (sidecar down) or the chart fails. */
export default function WidgetTable({ columns, rows }) {
  const { t, i18n } = useTranslation();
  const cols = Array.isArray(columns) ? columns : [];
  const list = Array.isArray(rows) ? rows : [];
  const names = cols.length ? cols.map(columnName) : (list[0] && !Array.isArray(list[0]) ? Object.keys(list[0]) : []);
  const locale = i18n.language && i18n.language.startsWith('es') ? 'es-CO' : 'en-US';

  if (!list.length) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">{t('Dashboards.widget.noRows')}</Typography>
      </Box>
    );
  }

  const cellValue = (row, name, idx) => (Array.isArray(row) ? row[idx] : row[name]);

  return (
    <TableContainer sx={{ height: '100%', overflow: 'auto' }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            {names.map((n) => (
              <TableCell key={n} sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{n}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {list.slice(0, MAX_DISPLAY_ROWS).map((row, ri) => (
            // eslint-disable-next-line react/no-array-index-key
            <TableRow key={ri}>
              {names.map((n, ci) => {
                const v = cellValue(row, n, ci);
                return (
                  <TableCell key={n} align={typeof v === 'number' ? 'right' : 'left'} sx={{ whiteSpace: 'nowrap' }}>
                    {formatCell(v, locale)}
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {list.length > MAX_DISPLAY_ROWS && (
        <Typography variant="caption" color="text.secondary" sx={{ p: 1, display: 'block' }}>
          {t('Dashboards.widget.rowsTruncated', { shown: MAX_DISPLAY_ROWS, total: list.length })}
        </Typography>
      )}
    </TableContainer>
  );
}
